<script setup lang="ts">
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-vue-next'
import { ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { searchPartnersPath } from '@/routes'

interface Partner {
  id: number
  name: string
}

const props = defineProps<{
  modelValue: string
  partnerName: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [string]
  'update:partnerName': [string]
}>()

const open = ref(false)
const loading = ref(false)
const hasLoaded = ref(false)
const results = ref<Partner[]>([])
const query = ref('')

let debounceTimer: ReturnType<typeof setTimeout>

// Reset query and refresh when opened; keep old results visible until new ones arrive
watch(open, (val) => {
  if (val) {
    query.value = ''
    fetchPartners('')
  }
})

watch(query, (q) => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => fetchPartners(q), 250)
})

async function fetchPartners(q: string) {
  loading.value = true
  try {
    const url = searchPartnersPath({ format: 'json', q: q || undefined })
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    results.value = await res.json()
  } finally {
    loading.value = false
    hasLoaded.value = true
  }
}

function select(partner: Partner) {
  emit('update:modelValue', String(partner.id))
  emit('update:partnerName', partner.name)
  open.value = false
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        role="combobox"
        :aria-expanded="open"
        :disabled="disabled"
        class="w-full justify-between font-normal"
      >
        <span :class="cn('truncate', !partnerName && 'text-muted-foreground')">
          {{ partnerName || 'Select partner…' }}
        </span>
        <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-(--reka-popover-trigger-width) p-0" align="start">
      <!-- Search bar: spinner replaces icon on subsequent fetches so the list never disappears -->
      <div
        class="border-border focus-within:border-ring flex items-center gap-2 border-b px-3 transition-colors"
      >
        <Loader2 v-if="loading" class="text-muted-foreground size-4 shrink-0 animate-spin" />
        <Search v-else class="text-muted-foreground size-4 shrink-0" />
        <Input
          v-model="query"
          type="text"
          placeholder="Search partners…"
          class="h-10 rounded-none border-0 px-0 shadow-none focus-visible:ring-0"
        />
      </div>
      <Command>
        <CommandList>
          <!-- Initial load: centered spinner keeps the popover at a stable height -->
          <div v-if="!hasLoaded" class="flex h-52 items-center justify-center">
            <Loader2 class="text-muted-foreground size-5 animate-spin" />
          </div>
          <template v-else>
            <p v-if="results.length === 0" class="text-muted-foreground py-6 text-center text-sm">
              No partners found.
            </p>
            <CommandGroup v-else>
              <CommandItem
                v-for="partner in results"
                :key="partner.id"
                :value="String(partner.id)"
                @select="select(partner)"
              >
                <Check
                  :class="cn('size-4', props.modelValue === String(partner.id) ? 'opacity-100' : 'opacity-0')"
                />
                {{ partner.name }}
              </CommandItem>
            </CommandGroup>
          </template>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
